/**
 * Declarative config of the claim « Charges » tab (`extranet.claimChargesCard`,
 * fiche 22742): stable columns, the « Total » row and the DataList config
 * builder.
 *
 * Stable column keys: `garantie` (LIB_GAR), `provision` (PROV_RESTANTE),
 * `reglement` (REG), `recoursEstime` (RECPROV), `recoursEncaisse` (RECREG),
 * `franchise` (FRANCH_SGAR). Amounts render `1 800,00 €`; a `null` amount
 * renders an empty cell.
 *
 * **Total row.** The fiche wants a bold « Total » line after the last
 * guarantee on desktop AND on mobile. DataList's native footer totals
 * (`total: true`) only render in the desktop `<table>` — its < 768px card
 * layout has no footer — so the Total is a clearly-marked extra row
 * appended client-side: stable row id `total`, flag `__total: true`, each
 * amount the column sum with `null` counted as 0 (rounded to the cent).
 * Being a regular row it follows every column override (rename / hide /
 * reorder) and shows up as the last mobile card too; the custom renderers
 * (`./claimChargesRenderers.tsx`) bold it and a row rule tints it. Sorting
 * is disabled so it always stays last; it is not counted as a guarantee
 * (the empty state still reads the real rows).
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import type { ClaimChargeRow } from '../../api/claimsService';
import { EMPTY_STATE_TEXT } from '../shared/cardText';
import { CLAIMS_TEXT } from './claimsText';

/** Renderer key of the amount columns (euros; bold on the Total row). */
export const CLAIM_CHARGE_AMOUNT_RENDERER = 'extranet-claimChargeAmount';

/** Renderer key of the `garantie` column (bold « Total » on the Total row). */
export const CLAIM_CHARGE_LABEL_RENDERER = 'extranet-claimChargeLabel';

/** Stable row id of the Total row. */
export const CLAIM_CHARGES_TOTAL_ROW_ID = 'total';

/** Flag carried by the Total row only. */
export const CLAIM_CHARGES_TOTAL_FLAG = '__total';

/** Stable column keys of the charges table. */
export type ClaimChargesColumnKey =
  | 'garantie'
  | 'provision'
  | 'reglement'
  | 'recoursEstime'
  | 'recoursEncaisse'
  | 'franchise';

/** The amount columns, summed on the Total row. */
export const CLAIM_CHARGE_AMOUNT_KEYS = [
  'provision',
  'reglement',
  'recoursEstime',
  'recoursEncaisse',
  'franchise',
] as const satisfies readonly ClaimChargesColumnKey[];

export type ClaimChargeAmountKey = (typeof CLAIM_CHARGE_AMOUNT_KEYS)[number];

/** Per-column sums of the Total row. */
export type ClaimChargeTotals = Record<ClaimChargeAmountKey, number>;

function amountColumn(label: string, order: number, headerTooltip?: string): ColumnConfig {
  return {
    label,
    order,
    type: 'custom',
    typeOptions: { renderer: CLAIM_CHARGE_AMOUNT_RENDERER },
    align: 'right',
    sortable: false,
    ...(headerTooltip === undefined ? {} : { headerTooltip }),
  };
}

/** Base columns (fiche order). */
export const CLAIM_CHARGES_COLUMNS: Readonly<Record<ClaimChargesColumnKey, ColumnConfig>> = {
  garantie: {
    label: 'Garantie',
    order: 1,
    type: 'custom',
    typeOptions: { renderer: CLAIM_CHARGE_LABEL_RENDERER },
    className: 'font-medium',
    sortable: false,
  },
  provision: amountColumn('Provision', 2, 'Provision restante'),
  reglement: amountColumn('Règlement', 3),
  recoursEstime: amountColumn('Recours estimé', 4),
  recoursEncaisse: amountColumn('Recours encaissé', 5),
  franchise: amountColumn('Franchise', 6),
};

/** Round a euro amount to the cent (absorbs float noise of the sums). */
function roundToCent(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/** Sum each amount column, `null` counted as 0. */
export function computeClaimChargeTotals(rows: readonly ClaimChargeRow[]): ClaimChargeTotals {
  const totals: ClaimChargeTotals = {
    provision: 0,
    reglement: 0,
    recoursEstime: 0,
    recoursEncaisse: 0,
    franchise: 0,
  };
  for (const row of rows) {
    for (const key of CLAIM_CHARGE_AMOUNT_KEYS) {
      totals[key] += row[key] ?? 0;
    }
  }
  for (const key of CLAIM_CHARGE_AMOUNT_KEYS) {
    totals[key] = roundToCent(totals[key]);
  }
  return totals;
}

/** The Total row appended after the guarantees. */
export interface ClaimChargesTotalRow extends ClaimChargeTotals {
  id: typeof CLAIM_CHARGES_TOTAL_ROW_ID;
  garantie: string;
  [CLAIM_CHARGES_TOTAL_FLAG]: true;
}

export function buildClaimChargesTotalRow(rows: readonly ClaimChargeRow[]): ClaimChargesTotalRow {
  return {
    id: CLAIM_CHARGES_TOTAL_ROW_ID,
    garantie: CLAIMS_TEXT.totalRow,
    ...computeClaimChargeTotals(rows),
    [CLAIM_CHARGES_TOTAL_FLAG]: true,
  };
}

/** `true` for the Total row of the charges table. */
export function isClaimChargesTotalRow(row: Record<string, unknown>): boolean {
  return row[CLAIM_CHARGES_TOTAL_FLAG] === true;
}

/**
 * Base DataList config: the guarantees in API order then the Total row (only
 * when there is at least one guarantee), one page, no toolbar, no sorting.
 */
export function buildClaimChargesConfig(
  componentId: string,
  rows: readonly ClaimChargeRow[],
): DataListConfig {
  const data: Record<string, unknown>[] =
    rows.length === 0
      ? []
      : [
          ...(rows as unknown as Record<string, unknown>[]),
          buildClaimChargesTotalRow(rows) as unknown as Record<string, unknown>,
        ];
  return {
    id: componentId,
    dataSource: { data },
    columns: { ...CLAIM_CHARGES_COLUMNS },
    display: { size: 'compact', hoverable: false },
    sorting: { enabled: false },
    toolbar: { visible: false },
    pagination: { mode: 'scroll', pageSize: 999_999 },
    formatting: {
      rowRules: [
        {
          condition: { field: CLAIM_CHARGES_TOTAL_FLAG, operator: 'equals', value: true },
          className: 'bg-secondary-50',
        },
      ],
    },
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
